import { Building2, Search } from "lucide-react";
import * as React from "react";

import type { InsightsPartnerPerformanceRow } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { SortableHeader } from "@/components/ui/sortable-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  tableRowLinkClassName,
} from "@/components/ui/table";
import { formatAudCents } from "@/lib/spif";
import { type SortValue } from "@/lib/tableSort";
import { useTableSort } from "@/lib/useTableSort";
import { cn } from "@/lib/utils";

export interface PartnerPerformanceLedgerProps {
  rows: InsightsPartnerPerformanceRow[];
  /** Bound to the search box; debounced by the caller before it hits the API. */
  searchValue: string;
  onSearchChange: (next: string) => void;
  /** CHP-864: whole-row click opens the per-CP drill-down modal. Omit to keep
   * the ledger static (rows render non-interactive). */
  onRowClick?: (partnerId: string) => void;
}

/** CHP-819: imperative handle so the header "Search channel partners" affordance
 * can scroll to + focus the ledger's own search box (mirrors the PO Insights
 * "Find team member" → directory-search pattern). */
export interface PartnerPerformanceLedgerHandle {
  focusSearch: () => void;
}

/** Sortable columns for the org-level Channel-Partner Performance ledger
 * (CHP-782). Order mirrors the visible columns. */
type PerformanceSortColumn =
  "name" | "status" | "live" | "pipeline" | "expiring" | "closed_won_fy" | "certified";

/** The v1 client-side Status filter (CHP-782): "all" plus the two derived
 * org-status values. A full faceted filter set is a follow-up — the designer is
 * to define the facets; for v1 the page ships only the search box + this Status
 * filter. */
type StatusFilter = "all" | "active" | "invited";

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
];

/** Module-level so the `useTableSort` memo dependency stays stable. */
function performanceSortValue(
  r: InsightsPartnerPerformanceRow,
  column: PerformanceSortColumn,
): SortValue {
  switch (column) {
    case "name":
      return r.company_name;
    case "status":
      return r.org_status;
    case "live":
      return r.live_deals_count;
    case "pipeline":
      return r.pipeline_value_cents;
    case "expiring":
      return r.expiring_30d_count;
    case "closed_won_fy":
      return r.closed_won_fy_cents;
    case "certified":
      return r.certified_pct;
  }
}

/** Status badge — active reads as a positive (success) state, invited as a
 * neutral outline chip (matches the badge variants the rest of Insights uses). */
function StatusBadge({ status }: { status: "active" | "invited" }) {
  return status === "active" ? (
    <Badge variant="success">Active</Badge>
  ) : (
    <Badge variant="outline">Invited</Badge>
  );
}

/**
 * Channel Partner Performance ledger (CHP-782) — a sortable org-level table for
 * the CM/RA/RM Program Insights page. One row per Channel-Partner organisation.
 *
 * Columns: Channel Partner Name, Status (Active/Invited badge), Live deals,
 * Pipeline $, Expiring ≤30d, Closed-won (FY) $, Certification %. The closed-won
 * column is the current Australian financial year to date (1 July – 30 June),
 * per the Flow spec (CHP-782). Money fields are cents on the wire → AUD via
 * `formatAudCents`; certification is `${pct}%`.
 *
 * v1 advanced filters: a search box (server-side `?q=` company-name substring,
 * debounced by the caller) plus a client-side Status filter. The full faceted
 * filter set is a follow-up — the designer is to define the facets.
 */
export const PartnerPerformanceLedger = React.forwardRef<
  PartnerPerformanceLedgerHandle,
  PartnerPerformanceLedgerProps
>(function PartnerPerformanceLedger({ rows, searchValue, onSearchChange, onRowClick }, ref) {
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => ({
    focusSearch() {
      searchInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      searchInputRef.current?.focus();
    },
  }));

  // Client-side Status filter (v1) — applied before the sort so the visible
  // ordering matches the filtered set.
  const filtered = React.useMemo(
    () => (statusFilter === "all" ? rows : rows.filter((r) => r.org_status === statusFilter)),
    [rows, statusFilter],
  );

  const { sort, toggle, sortedRows } = useTableSort(filtered, performanceSortValue);

  return (
    <div className="space-y-4">
      {/* v1 advanced filters: search box (server `?q=`) + client Status filter. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-md">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            ref={searchInputRef}
            type="search"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search channel partners…"
            aria-label="Search channel partners"
            className="pl-9"
            data-testid="partner-performance-search"
          />
        </div>
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label="Filter by status"
          data-testid="partner-performance-status-filter"
        >
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              aria-pressed={statusFilter === f.value}
              data-testid={`partner-performance-status-${f.value}`}
              className={
                statusFilter === f.value
                  ? "rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
                  : "rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {sortedRows.length === 0 ? (
        <EmptyState icon={Building2} title="No channel partners match this view." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {/* Column header copy (CHP-782), not a role-vocabulary token. */}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <SortableHeader column="name" sort={sort} onToggle={toggle}>
                Channel Partner Name
              </SortableHeader>
              <SortableHeader column="status" sort={sort} onToggle={toggle}>
                Status
              </SortableHeader>
              <SortableHeader column="live" sort={sort} onToggle={toggle} align="right">
                Live deals
              </SortableHeader>
              <SortableHeader column="pipeline" sort={sort} onToggle={toggle} align="right">
                Pipeline $
              </SortableHeader>
              <SortableHeader column="expiring" sort={sort} onToggle={toggle} align="right">
                Expiring ≤30d
              </SortableHeader>
              <SortableHeader column="closed_won_fy" sort={sort} onToggle={toggle} align="right">
                <span title="This financial year (1 July – 30 June)">Closed-won (FY) $</span>
              </SortableHeader>
              <SortableHeader column="certified" sort={sort} onToggle={toggle} align="right">
                Certification %
              </SortableHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.map((r) => (
              <TableRow
                key={r.partner_id}
                data-testid={`partner-performance-row-${r.partner_id}`}
                // CHP-864: whole-row click opens the per-CP drill-down modal. The
                // row's own onClick is the Safari-safe cross-browser hit path
                // (same idiom as PoDealLedger / CHP-832/833/865).
                interactive={!!onRowClick}
                onClick={onRowClick ? () => onRowClick(r.partner_id) : undefined}
              >
                <TableCell className="font-medium">
                  {onRowClick ? (
                    // Keyboard-focusable, named primary control; stopPropagation
                    // so the row + button don't both fire.
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(r.partner_id);
                      }}
                      className={cn(
                        "text-left font-medium text-primary underline-offset-4 hover:underline",
                        tableRowLinkClassName,
                      )}
                      data-testid={`partner-performance-open-${r.partner_id}`}
                    >
                      {r.company_name}
                    </button>
                  ) : (
                    r.company_name
                  )}
                </TableCell>
                <TableCell data-testid={`partner-performance-status-cell-${r.partner_id}`}>
                  <StatusBadge status={r.org_status} />
                </TableCell>
                <TableCell className="text-right tabular-nums">{r.live_deals_count}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatAudCents(r.pipeline_value_cents)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{r.expiring_30d_count}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatAudCents(r.closed_won_fy_cents)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{r.certified_pct}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
});
